import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Categories, Category } from 'interfaces/documents';
import {
    fetchCategories,
    fetchCategoriesMap,
    moveCategory,
    renameCategory,
    softDeleteCategory,
} from './categoriesActions';
import { BreadcrumbItemType } from '../types';

interface CategoriesState {
    list: Categories;
    map: any[];
    excluded: number[];
    parent: Category | null;
    breadcrumbs: BreadcrumbItemType[];
    loading: boolean;
    error: any | null;
    params: any;
}

interface ParamsState {
    [key: string]: any;
    query?: string | null;
    excluded_ids?: string[] | null;
}

export const initialState: CategoriesState = {
    list: [],
    map: [],
    excluded: [],
    breadcrumbs: [],
    parent: null,
    loading: false,
    error: null,
    params: {},
};

const categoriesSlice = createSlice({
    name: 'categories',
    initialState,
    reducers: {
        reset: () => initialState,
        setParams: (state, action: PayloadAction<ParamsState>) => {
            let preparams = JSON.parse(JSON.stringify(state.params));
            Object.keys(action.payload).forEach((key) => {
                preparams[key] = action.payload[key];
            });
            state.params = preparams;
        },
        setExcluded: (state, action: PayloadAction<Categories>) => {
            state.excluded = action.payload.map((item) => {
                return item.id;
            });
        },
        deleteFromMap: (state, action: PayloadAction<any>) => {
            delete state.map[action.payload];
        },
        resetMap: (state) => {
            state.map = [];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCategories.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchCategories.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;

            if (!action.payload) return;

            state.list = action.payload.data.list;
            state.parent = action.payload.data.parent;
            state.breadcrumbs = action.payload.data.breadcrumbs;
        });
        builder.addCase(fetchCategories.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(fetchCategoriesMap.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchCategoriesMap.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;

            if (!action.payload) return;
            if (action.payload.data.list.length === 0) return;

            const list = action.payload.data.list;
            const parent = action.payload.data.parent;

            if (parent) {
                state.map[parent.id] = list;
            }
        });
        builder.addCase(fetchCategoriesMap.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(softDeleteCategory.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(softDeleteCategory.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(softDeleteCategory.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(renameCategory.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(renameCategory.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(renameCategory.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(moveCategory.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(moveCategory.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(moveCategory.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });
    },
});

export const { setExcluded, deleteFromMap, reset, resetMap, setParams } = categoriesSlice.actions;
export default categoriesSlice.reducer;
